import React from 'react';
import { 
  CheckCircleIcon, 
  ExclamationTriangleIcon, 
  XCircleIcon,
  ArrowPathIcon 
} from '@heroicons/react/24/outline';

const UploadProgress = ({ 
  isUploading = false, 
  progress = 0, 
  status = 'idle', // idle, uploading, success, error
  results = null,
  onClose,
  className = ''
}) => {
  if (!isUploading && !results) return null;

  const getStatusIcon = () => {
    switch (status) {
      case 'uploading':
        return <ArrowPathIcon className="w-5 h-5 text-orange-500 animate-spin" />;
      case 'success':
        return <CheckCircleIcon className="w-5 h-5 text-emerald-500" />;
      case 'error':
        return <XCircleIcon className="w-5 h-5 text-red-500" />;
      default:
        return null;
    }
  };

  const getStatusColor = () => {
    switch (status) {
      case 'uploading':
        return 'border-orange-200 dark:border-orange-700 bg-orange-50 dark:bg-orange-900/20';
      case 'success':
        return 'border-emerald-200 dark:border-emerald-700 bg-emerald-50 dark:bg-emerald-900/20';
      case 'error':
        return 'border-red-200 dark:border-red-700 bg-red-50 dark:bg-red-900/20';
      default:
        return 'border-gray-200 dark:border-gray-800 bg-gray-50 dark:bg-[#1a1a1a]';
    }
  };

  return (
    <div className={`border rounded-lg p-4 ${getStatusColor()} ${className}`}>
      <div className="flex items-start justify-between">
        <div className="flex items-center space-x-3 flex-1">
          {getStatusIcon()}
          <div className="flex-1">
            <h4 className="text-sm font-medium text-gray-900 dark:text-gray-100">
              {status === 'uploading' && 'Importing Users...'}
              {status === 'success' && 'Import Completed'}
              {status === 'error' && 'Import Failed'}
            </h4>
            
            {status === 'uploading' && (
              <div className="mt-2">
                <div className="flex items-center justify-between text-xs text-gray-600 dark:text-gray-400 mb-1">
                  <span>Processing CSV file...</span>
                  <span>{progress}%</span>
                </div>
                <div className="w-full bg-gray-200 dark:bg-[#222222] rounded-full h-2">
                  <div 
                    className="bg-orange-500 h-2 rounded-full transition-all duration-300"
                    style={{ width: `${progress}%` }}
                  />
                </div>
              </div>
            )}

            {results && (
              <div className="mt-3 space-y-2">
                {/* Summary Stats */}
                <div className="grid grid-cols-2 md:grid-cols-4 gap-2 text-xs">
                  <div className="bg-white/60 dark:bg-[#222222]/60 rounded px-2 py-1">
                    <div className="font-medium text-gray-900 dark:text-gray-100">{results.summary?.totalProcessed || 0}</div>
                    <div className="text-gray-600 dark:text-gray-400">Total</div>
                  </div>
                  <div className="bg-white/60 dark:bg-[#222222]/60 rounded px-2 py-1">
                    <div className="font-medium text-emerald-600 dark:text-emerald-400">{results.summary?.successfullyCreated || 0}</div>
                    <div className="text-gray-600 dark:text-gray-400">Created</div>
                  </div>
                  <div className="bg-white/60 dark:bg-[#222222]/60 rounded px-2 py-1">
                    <div className="font-medium text-amber-600 dark:text-amber-400">{results.summary?.skipped || 0}</div>
                    <div className="text-gray-600 dark:text-gray-400">Skipped</div>
                  </div>
                  <div className="bg-white/60 dark:bg-[#222222]/60 rounded px-2 py-1">
                    <div className="font-medium text-red-600 dark:text-red-400">{results.summary?.errors || 0}</div>
                    <div className="text-gray-600 dark:text-gray-400">Errors</div>
                  </div>
                </div>

                {/* Error Details */}
                {results.details?.errors && results.details.errors.length > 0 && (
                  <div className="mt-3">
                    <details className="group">
                      <summary className="cursor-pointer text-xs font-medium text-red-700 dark:text-red-400 hover:text-red-800 dark:hover:text-red-300">
                        View {results.details.errors.length} Error{results.details.errors.length > 1 ? 's' : ''}
                      </summary>
                      <div className="mt-2 max-h-32 overflow-y-auto bg-white/60 dark:bg-[#222222]/60 rounded border border-gray-200 dark:border-gray-700">
                        {results.details.errors.map((error, index) => (
                          <div key={index} className="px-2 py-1 text-xs border-b border-gray-200 dark:border-gray-700 last:border-b-0">
                            <div className="font-medium text-red-700 dark:text-red-400">Row {error.row}: {error.email}</div>
                            <div className="text-red-600 dark:text-red-500">{error.error}</div>
                          </div>
                        ))}
                      </div>
                    </details>
                  </div>
                )}

                {/* Skipped Details */}
                {results.details?.skipped && results.details.skipped.length > 0 && (
                  <div className="mt-3">
                    <details className="group">
                      <summary className="cursor-pointer text-xs font-medium text-amber-700 dark:text-amber-400 hover:text-amber-800 dark:hover:text-amber-300">
                        View {results.details.skipped.length} Skipped User{results.details.skipped.length > 1 ? 's' : ''}
                      </summary>
                      <div className="mt-2 max-h-32 overflow-y-auto bg-white/60 dark:bg-[#222222]/60 rounded border border-gray-200 dark:border-gray-700">
                        {results.details.skipped.map((skipped, index) => (
                          <div key={index} className="px-2 py-1 text-xs border-b border-gray-200 dark:border-gray-700 last:border-b-0">
                            <div className="font-medium text-amber-700 dark:text-amber-400">Row {skipped.row}: {skipped.email}</div>
                            <div className="text-amber-600 dark:text-amber-500">{skipped.reason}</div>
                          </div>
                        ))}
                      </div>
                    </details>
                  </div>
                )}

                {/* Success Details */}
                {results.details?.successful && results.details.successful.length > 0 && (
                  <div className="mt-3">
                    <details className="group">
                      <summary className="cursor-pointer text-xs font-medium text-emerald-700 dark:text-emerald-400 hover:text-emerald-800 dark:hover:text-emerald-300">
                        View {results.details.successful.length} Created User{results.details.successful.length > 1 ? 's' : ''}
                      </summary>
                      <div className="mt-2 max-h-32 overflow-y-auto bg-white/60 dark:bg-[#222222]/60 rounded border border-gray-200 dark:border-gray-700">
                        {results.details.successful.map((user, index) => (
                          <div key={index} className="px-2 py-1 text-xs border-b border-gray-200 dark:border-gray-700 last:border-b-0">
                            <div className="font-medium text-emerald-700 dark:text-emerald-400">{user.name}</div>
                            <div className="text-emerald-600 dark:text-emerald-500">{user.email} • {user.role}</div>
                            {user.generatedPassword && (
                              <div className="text-xs text-gray-600 dark:text-gray-400 mt-1 font-mono bg-gray-100 dark:bg-[#222222] px-1 rounded">
                                Generated Password: {user.generatedPassword}
                              </div>
                            )}
                          </div>
                        ))}
                      </div>
                    </details>
                  </div>
                )}
              </div>
            )}
          </div>
        </div>
        
        {onClose && !isUploading && (
          <button
            onClick={onClose}
            className="ml-3 text-gray-400 dark:text-gray-500 hover:text-gray-600 dark:hover:text-gray-400 focus:outline-none"
          >
            <XCircleIcon className="w-5 h-5" />
          </button>
        )}
      </div>
    </div>
  );
};

export default UploadProgress;